import { useCallback } from "react";
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAPI } from "./useAPIs";
const PAGE_SIZE = "200";

export function useTimelineInfiniteQuery(debouncedFilters, createIsoStringWithTimezone, dateType, ordering) {
  const { apiCall } = useAPI();

  const buildQueryString = useCallback((filterObj, page) => {
    const params = new URLSearchParams();
    if (filterObj.vin?.length > 0) params.append('vin', filterObj.vin.join(','));
    if (filterObj.mac_id?.length > 0) params.append('mac_id', filterObj.mac_id.join(','));
    if (filterObj.file_type?.length > 0) params.append('file_type', filterObj.file_type.join(','));
    if (filterObj.category?.length > 0) params.append('category', filterObj.category.join(','));
    
    if (filterObj.event_type?.length > 0) {
      const groupedByType = filterObj.event_type.reduce((acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        let formattedValue = item.value.replace(/\s+/g, '-');
        if (item.status) {
          formattedValue += `:${item.status}`;
        }
        acc[item.type].push(formattedValue);
        return acc;
      }, {});
      Object.entries(groupedByType).forEach(([type, values]) => {
        params.append(type, values.join(','));
      });
    }

    if (filterObj.start_datetime) params.append('start_date', createIsoStringWithTimezone(filterObj.start_datetime));
    if (filterObj.end_datetime) params.append('end_date', createIsoStringWithTimezone(filterObj.end_datetime));
    if (dateType) {
      params.append('use_datetime_type', dateType);
    }
    if (ordering) {
      params.append('ordering', ordering);
    }
    params.append('page', page.toString());
    params.append('page_size', PAGE_SIZE);
    
    return params.toString();
  }, [createIsoStringWithTimezone, ordering]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
    isError,
    isFetching
  } = useInfiniteQuery({
    queryKey: ['timeline-infinite', debouncedFilters, dateType, ordering],
    queryFn: async ({ pageParam = 1 }) => {
      const queryString = buildQueryString(debouncedFilters, pageParam);
      const response = await apiCall('GET', `events/by-logger?${queryString}`);
    
      return {
        data: response.data.results || [],
        page: pageParam,
        totalPages: response.data.total_pages || 1,
        totalCount: response.data.count || 1,
        hasNextPage: pageParam < (response.data.total_pages || 1),
        currentPage: response.data.current_page || pageParam,
        pageSize: response.data.page_size || PAGE_SIZE,
        next: response.data.next || false,
        previous: response.data.previous || false
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, 
    retry: 3, 
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: !!(debouncedFilters.vin?.length > 0 || debouncedFilters.mac_id?.length > 0),
  });

  const flattenedData = data?.pages?.flatMap(page => page.data) || [];
  const hasData = flattenedData.length > 0;
  const isEmpty = !isLoading && !hasData;

  return {
    data: flattenedData,
    rawData: data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
    isError,
    isFetching,
    reset: () => {
      refetch();
    },
    isEmpty,
    hasData,
    totalCount: data?.pages?.[data.pages.length - 1]?.totalCount,
    totalPages: data?.pages?.[data.pages.length - 1]?.totalPages,
    currentPage: data?.pages?.[data.pages.length - 1]?.currentPage,
    pageSize: data?.pages?.[data.pages.length - 1]?.pageSize,
  };
}